
import { apiClient } from '@/lib/apiClient';
import { fetchLeadWithContacts } from '@/services/api/leads';

export interface LeadContact {
  id: string | number;
  first_name: string;
  last_name: string;
  name: string;
  email: string | Array<{email: string; type: "official" | "personal" | "cell" | "others"; is_primary: boolean}>; // Support both string and JSON array with required properties
  phone?: string | Array<{phone: string; type: "official" | "personal" | "cell" | "others"; is_primary: boolean}>; // Support both string and JSON array with required properties
  phone_number?: string;
  // New array structures for multiple contact methods
  phones?: Array<{ number: string; isPrimary: boolean }>;
  emails?: Array<{ address: string; isPrimary: boolean }>;
  designation?: string; // Primary field for job title
  department?: string;
  is_decision_maker?: boolean;
  decision_maker?: boolean;
  is_primary?: boolean; // Primary contact flag
  status?: string;
  contact_status?: string;
  lead_company_id: string;
  created_by?: number | null;
  created_at: string;
  updated_at: string;
  
  // URLs object for multiple social/professional links
  urls?: {
    linkedin?: string;
    twitter?: string;
    website?: string;
    github?: string;
    portfolio?: string;
    blog?: string;
    youtube?: string;
  };
  
  // Additional properties used by components
  avatar_url?: string;
  linkedin_url?: string; // Keep for backward compatibility
  twitter_url?: string;
  influence_score?: number;
  engagement_score?: number;
  lead_score?: number;
  sentiment_score?: number;
  last_contact?: string;
  last_contact_date?: string;
  notes?: string;
  
  // Enhanced metadata for rich contact information
  metadata?: {
    source?: string;
    last_contact_method?: string;
    preferred_contact_time?: string;
    interests?: string[];
    tags?: string[];
    priority?: string;
    last_contact_notes?: string;
    next_follow_up?: string;
    demo_scheduled?: string;
    deal_stage?: string;
    potential_deal_size?: string;
    decision_timeline?: string;
    [key: string]: any;
  };
  
  // Company information
  company?: {
    name: string;
    website_url?: string;
    industry?: string;
    company_size?: string;
    logo_url?: string;
  };
  company_name?: string;
  website_url?: string;
  industry?: string;
  company_size?: string;
  company_info?: string;
  logo_url?: string;
  
  // Address information
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zip_code?: string;
  
  // Additional fields
  target_market?: string;
  pain_points?: string;
  ai_summary?: string;
}

// Generate mock contacts data
const generateMockContacts = (): LeadContact[] => {
  console.log('📞 leadContactsService: Generating mock contacts');
  return [
    {
      id: '1',
      first_name: 'John',
      last_name: 'Smith',
      name: 'John Smith',
      email: [
        { email: 'john.smith@techcorp.example.com', type: 'official', is_primary: true },
        { email: 'john.smith@personal.com', type: 'personal', is_primary: false }
      ],
      phone: [
        { phone: '+1-555-0123', type: 'official', is_primary: true },
        { phone: '+1-555-0124', type: 'cell', is_primary: false }
      ],
      phone_number: '+1-555-0123',
      designation: 'CTO',
      department: 'Technology',
      is_decision_maker: true,
      decision_maker: true,
      is_primary: true,
      status: 'Active',
      lead_company_id: '1',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      avatar_url: undefined,
      linkedin_url: 'https://linkedin.com/in/johnsmith',
      urls: {
        linkedin: 'https://linkedin.com/in/johnsmith',
        twitter: 'https://twitter.com/johnsmith',
        website: 'https://johnsmith.dev',
        github: 'https://github.com/johnsmith'
      },
      influence_score: 90,
      engagement_score: 85,
      lead_score: 88,
      company: {
        name: 'TechCorp Solutions',
        website_url: 'https://techcorp.example.com',
        industry: 'Technology',
        company_size: '50-200'
      }
    },
    {
      id: '2',
      first_name: 'Sarah',
      last_name: 'Johnson',
      name: 'Sarah Johnson',
      email: [
        { email: 'sarah.johnson@greenenergy.example.com', type: 'official', is_primary: true },
        { email: 's.johnson@outlook.com', type: 'personal', is_primary: false }
      ],
      phone: [
        { phone: '+1-555-0124', type: 'official', is_primary: true },
        { phone: '+1-555-0125', type: 'cell', is_primary: false },
        { phone: '+1-555-0126', type: 'personal', is_primary: false }
      ],
      phone_number: '+1-555-0124',
      designation: 'VP of Sales',
      department: 'Sales',
      is_decision_maker: true,
      decision_maker: true,
      is_primary: false,
      status: 'Active',
      lead_company_id: '2',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      avatar_url: undefined,
      linkedin_url: 'https://linkedin.com/in/sarahjohnson',
      influence_score: 95,
      engagement_score: 90,
      lead_score: 92,
      company: {
        name: 'Green Energy Co',
        website_url: 'https://greenenergy.example.com',
        industry: 'Energy',
        company_size: '200-500'
      }
    },
    {
      id: '3',
      first_name: 'Mike',
      last_name: 'Chen',
      name: 'Mike Chen',
      email: [
        { email: 'mike.chen@techcorp.example.com', type: 'official', is_primary: true }
      ],
      phone: [
        { phone: '+1-555-0125', type: 'official', is_primary: true }
      ],
      phone_number: '+1-555-0125',
      designation: 'Engineering Manager',
      department: 'Technology',
      is_decision_maker: false,
      decision_maker: false,
      is_primary: false,
      status: 'Active',
      lead_company_id: '1',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      avatar_url: undefined,
      linkedin_url: 'https://linkedin.com/in/mikechen',
      influence_score: 75,
      engagement_score: 80,
      lead_score: 78,
      company: {
        name: 'TechCorp Solutions',
        website_url: 'https://techcorp.example.com',
        industry: 'Technology',
        company_size: '50-200'
      }
    }
  ];
};

/**
 * Maps a raw API contact object to our LeadContact interface.
 * Per the new contract: phones/emails are always arrays, urls/metadata always
 * objects, every other key present (null when empty).
 */
const mapApiContact = (contact: any): LeadContact => {
  // Parse email data - handle both string and array formats
  let emailData = contact.emails || contact.email;
  if (typeof emailData === 'string' && emailData.trim()) {
    emailData = [{ email: emailData, type: 'official', is_primary: true }];
  } else if (Array.isArray(emailData)) {
    emailData = emailData.filter((e: any) => e?.email?.trim());
  } else {
    emailData = [];
  }

  if (emailData.length === 0 && contact.metadata?.email && String(contact.metadata.email).trim()) {
    emailData = [{ email: contact.metadata.email, type: 'official', is_primary: true }];
  }

  // Parse phone data - handle both string and array formats
  let phoneData = contact.phones || contact.phone;
  if (typeof phoneData === 'string' && phoneData.trim()) {
    phoneData = [{ phone: phoneData, type: 'official', is_primary: true }];
  } else if (Array.isArray(phoneData)) {
    phoneData = phoneData.filter((p: any) => p?.phone?.trim());
  } else {
    phoneData = [];
  }

  if (phoneData.length === 0 && contact.metadata?.phone && String(contact.metadata.phone).trim()) {
    phoneData = [{ phone: contact.metadata.phone, type: 'official', is_primary: true }];
  }

  return {
    id: contact.id ?? contact.contact_id,
    first_name: contact.first_name || '',
    last_name: contact.last_name || '',
    name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
    email: emailData,
    phone: phoneData,
    phones: phoneData,
    emails: emailData,
    phone_number: contact.phone_number ? String(contact.phone_number) :
      ((Array.isArray(phoneData) && phoneData.length > 0) ? String(phoneData[0].phone) :
      (contact.metadata?.phone ? String(contact.metadata.phone) : '')),
    designation: contact.designation || '',
    department: contact.department || '',
    is_decision_maker: contact.is_primary || false,
    decision_maker: contact.is_primary || false,
    is_primary: contact.is_primary || false,
    status: contact.contact_status || 'Active',
    contact_status: contact.contact_status || 'Active',
    lead_company_id: contact.lead_id?.toString() || '',
    created_by: contact.created_by ?? null,
    created_at: contact.created_at || new Date().toISOString(),
    updated_at: contact.updated_at || new Date().toISOString(),
    last_contact_date: contact.last_contact_date,
    notes: contact.notes || '',
    urls: contact.urls || {},
    metadata: contact.metadata || {},
    company_name: contact.company_name || contact.company?.name || '',
    company: contact.company ? {
      name: contact.company.name || contact.company_name || '',
      website_url: contact.company.website_url || '',
      industry: contact.company.industry || '',
      company_size: contact.company.company_size || ''
    } : (contact.company_name ? { name: contact.company_name, website_url: '', industry: '', company_size: '' } : undefined)
  } as LeadContact;
};

export interface ContactsQueryParams {
  page?: number;
  per_page?: number;
  search?: string;
  status?: string;
  created_by?: number;
}

export interface PaginatedContactsResponse {
  items: LeadContact[];
  total_count: number;
  page: number;
  per_page: number;
}

/**
 * GET /contacts/ — paginated contacts list.
 * Query params: page, per_page (max 100), search, status, created_by (AND-ed).
 */
export const fetchContactsPaginated = async (
  params: ContactsQueryParams = {}
): Promise<PaginatedContactsResponse> => {
  const page = Math.max(1, params.page ?? 1);
  const perPage = Math.min(100, Math.max(1, params.per_page ?? 50));

  const query = new URLSearchParams();
  query.set('page', String(page));
  query.set('per_page', String(perPage));
  if (params.search?.trim()) query.set('search', params.search.trim());
  if (params.status?.trim()) query.set('status', params.status.trim());
  if (params.created_by !== undefined && params.created_by !== null) {
    query.set('created_by', String(params.created_by));
  }

  try {
    console.log(`📞 leadContactsService: GET /contacts/?${query.toString()}`);
    const data = await apiClient.get(`/contacts/?${query.toString()}`);

    // New contract: { items, total_count, page, per_page }
    if (data && Array.isArray(data.items)) {
      return {
        items: data.items.map(mapApiContact),
        total_count: typeof data.total_count === 'number' ? data.total_count : data.items.length,
        page: typeof data.page === 'number' ? data.page : page,
        per_page: typeof data.per_page === 'number' ? data.per_page : perPage
      };
    }

    // Legacy fallbacks (bare array / { contacts } / { data })
    const legacyList: any[] = Array.isArray(data)
      ? data
      : Array.isArray(data?.contacts)
        ? data.contacts
        : Array.isArray(data?.data)
          ? data.data
          : [];

    if (legacyList.length === 0) {
      return { items: [], total_count: 0, page, per_page: perPage };
    }

    return {
      items: legacyList.map(mapApiContact),
      total_count: legacyList.length,
      page,
      per_page: perPage
    };
  } catch (error) {
    console.error('📞 leadContactsService: Error in fetchContactsPaginated:', error);
    throw error;
  }
};

/**
 * @deprecated Prefer fetchContactsPaginated. Returns a single page of contacts.
 */
export const fetchAllContacts = async (
  params: ContactsQueryParams = {}
): Promise<LeadContact[]> => {
  try {
    const response = await fetchContactsPaginated({ per_page: 100, ...params });
    return response.items;
  } catch (error) {
    console.error('📞 leadContactsService: Error in fetchAllContacts:', error);
    console.log('📞 leadContactsService: Falling back to mock contacts data');
    return generateMockContacts();
  }
};



export const fetchContactById = async (contactId: string): Promise<{ contact: LeadContact; lead: any }> => {
  try {
    console.log(`📞 leadContactsService: fetchContactById called for ID: ${contactId}`);
    console.log(`📞 leadContactsService: Making API request to /contacts/${contactId}`);
    const data = await apiClient.get(`/contacts/${contactId}`);
    
    console.log('📞 leadContactsService: Raw contact detail response:', data);
    
    // Handle the new API response structure: { contact: {...}, lead: {...} }
    if (data && data.contact) {
      const contact = data.contact;
      const lead = data.lead;
      
      // Parse email data - handle both string and array formats
      let emailData = contact.email;
      console.log('📧 fetchContactById: Raw email data:', emailData);
      if (typeof emailData === 'string' && emailData.trim()) {
        emailData = [{ email: emailData, type: 'official', is_primary: true }];
      } else if (Array.isArray(emailData)) {
        emailData = emailData.filter(e => e.email?.trim());
      } else {
        emailData = [];
      }
      
      // Fallback to metadata email if no emails found
      if (emailData.length === 0 && contact.metadata?.email && contact.metadata.email.trim()) {
        emailData = [{ email: contact.metadata.email, type: 'official', is_primary: true }];
      }

      // Parse phone data - handle both string and array formats
      let phoneData = contact.phone;
      console.log('📞 fetchContactById: Raw phone data:', phoneData);
      if (typeof phoneData === 'string' && phoneData.trim()) {
        phoneData = [{ phone: phoneData, type: 'official', is_primary: true }];
      } else if (Array.isArray(phoneData)) {
        phoneData = phoneData.filter(p => p.phone?.trim());
      } else {
        phoneData = [];
      }
      
      // Fallback to metadata phone if no phones found
      if (phoneData.length === 0 && contact.metadata?.phone && contact.metadata.phone.trim()) {
        phoneData = [{ phone: contact.metadata.phone, type: 'official', is_primary: true }];
      }

      // Parse phones array from JSON string if needed
      let phonesArray = contact.phones;
      console.log('📞 fetchContactById: Raw phones array:', phonesArray);
      if (typeof phonesArray === 'string') {
        try {
          phonesArray = JSON.parse(phonesArray);
        } catch (e) {
          console.log('📞 fetchContactById: Failed to parse phones JSON:', e);
          phonesArray = [];
        }
      }
      if (!Array.isArray(phonesArray)) {
        phonesArray = [];
      }

      // Parse emails array from JSON string if needed  
      let emailsArray = contact.emails;
      console.log('📧 fetchContactById: Raw emails array:', emailsArray);
      if (typeof emailsArray === 'string') {
        try {
          emailsArray = JSON.parse(emailsArray);
        } catch (e) {
          console.log('📧 fetchContactById: Failed to parse emails JSON:', e);
          emailsArray = [];
        }
      }
      if (!Array.isArray(emailsArray)) {
        emailsArray = [];
      }

      const mappedContact: LeadContact = {
        id: contact.contact_id || contact.id,
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        name: `${contact.first_name || ''} ${contact.last_name || ''}`.trim(),
        email: emailData,
        phone: phoneData,
        phone_number: contact.phone_number || contact.phone || 
          (Array.isArray(phoneData) && phoneData.length > 0 ? phoneData[0].phone : 
          (contact.metadata?.phone || '')),
        // Map new array structures - use parsed arrays
        phones: phonesArray,
        emails: emailsArray,
        designation: contact.designation || '',
        department: contact.department || '',
        is_decision_maker: contact.is_primary || false,
        decision_maker: contact.is_primary || false,
        is_primary: contact.is_primary || false,
        status: contact.contact_status || 'Active',
        contact_status: contact.contact_status || 'Active',
        lead_company_id: contact.lead_id?.toString() || lead?.lead_id?.toString() || '',
        created_at: contact.created_at || new Date().toISOString(),
        updated_at: contact.updated_at || new Date().toISOString(),
        last_contact_date: contact.last_contact_date,
        notes: contact.notes || '',
        urls: contact.urls || {},
        metadata: contact.metadata || {},
        company: lead ? {
          name: lead.name || '',
          website_url: lead.website_url || '',
          industry: lead.industry || '',
          company_size: lead.company_size || ''
        } : undefined
      };
      
      console.log('📞 leadContactsService: Successfully mapped contact details');
      return { contact: mappedContact, lead };
    } else {
      console.log('📞 leadContactsService: Invalid response structure, using mock data');
      const mockContacts = generateMockContacts();
      const mockContact = mockContacts[0];
      return { 
        contact: mockContact, 
        lead: {
          lead_id: 1,
          name: mockContact.company?.name || 'Mock Company',
          website_url: mockContact.company?.website_url || '',
          industry: mockContact.company?.industry || '',
          company_size: mockContact.company?.company_size || ''
        }
      };
    }
  } catch (error) {
    console.error(`📞 leadContactsService: Error in fetchContactById for ID ${contactId}:`, error);
    console.log('📞 leadContactsService: Falling back to mock contact data');
    const mockContacts = generateMockContacts();
    const mockContact = mockContacts[0];
    return { 
      contact: mockContact, 
      lead: {
        lead_id: 1,
        name: mockContact.company?.name || 'Mock Company',
        website_url: mockContact.company?.website_url || '',
        industry: mockContact.company?.industry || '',
        company_size: mockContact.company?.company_size || ''
      }
    };
  }
};

export const createContact = async (contactData: Omit<LeadContact, 'id' | 'created_at' | 'updated_at'>): Promise<LeadContact> => {
  try {
    console.log('📝 Creating new contact with data:', contactData);
    // Ensure created_by is present; fallback to localStorage user id if missing
    const payload: any = { ...contactData };
    if (payload.created_by == null) {
      try {
        const userStr = localStorage.getItem('user');
        const uid = userStr ? JSON.parse(userStr).id : null;
        if (typeof uid === 'number') payload.created_by = uid;
      } catch (e) {
        console.warn('⚠️ Unable to infer created_by from localStorage:', e);
      }
    }
    const response = await apiClient.post('/contacts', payload);
    console.log('✅ Contact creation successful:', response);
    return response;
  } catch (error) {
    console.error('💥 Error creating contact:', error);
    
    // Fallback to mock response for development
    const newContact: LeadContact = {
      id: Date.now().toString(),
      ...(contactData as any),
      name: `${(contactData as any).first_name} ${(contactData as any).last_name}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    console.log('🔄 Using mock contact creation:', newContact);
    return newContact;
  }
};

export const updateLeadContact = async (contactId: string, updateData: Partial<LeadContact>): Promise<LeadContact> => {
  try {
    console.log(`📝 Updating contact ${contactId} with data:`, updateData);
    console.log(`🔍 Metadata being sent to API:`, updateData.metadata);
    
    // Keep metadata as object for the API
    const processedData = { ...updateData };
    console.log(`📡 Metadata being sent as object:`, processedData.metadata);
    console.log(`📡 Final data being sent to API:`, processedData);
    
    const response = await apiClient.put(`/contacts/${contactId}`, processedData);
    console.log(`✅ Contact ${contactId} update successful:`, response);
    return response;
  } catch (error) {
    console.error(`💥 Error updating contact ${contactId}:`, error);
    
    // Fallback to mock response for development
    const mockContacts = generateMockContacts();
    const existingContact = mockContacts.find(c => c.id === contactId) || mockContacts[0];
    
    return {
      ...existingContact,
      ...updateData,
      updated_at: new Date().toISOString()
    };
  }
};

export const deleteLeadContact = async (contactId: string): Promise<void> => {
  try {
    console.log(`🗑️ Deleting contact ${contactId}`);
    await apiClient.delete(`/contacts/${contactId}`);
    console.log(`✅ Contact ${contactId} deletion successful`);
  } catch (error) {
    console.error(`💥 Error deleting contact ${contactId}:`, error);
    console.log(`🔄 Contact ${contactId} deletion processed (mock)`);
  }
};

// Export other contact-related functions
export * from './leads/contactsApi';
